/** Device operations (spec §11). */

import { z } from "zod";
import { publicTenantlessOp, readOp, writeOp, zEpochMs, zId } from "./common.js";

/** B1110 — a typed pairing code at the door (`PAIR-XXXX` is 9; the domain judges the pattern). */
export const DEVICE_CODE_MAX = 32;

/** Spec §4.1 device types — POS is a channel, not a device (decision 2026-07-11, Pathik). */
const zDeviceRole = z.enum(["VALIDATOR", "TERMINAL", "KIOSK", "PRINTER"]);
const zDeviceStatus = z.enum(["ACTIVE", "REVOKED"]);

/**
 * Admin-gated (§11): device-code pairing, credentials scoped to role + location.
 *
 * B20a (ruling 2026-07-11, §9.1 device-pairing semantics): the response
 * delivers the credential secret EXACTLY ONCE, at enrollment, over TLS.
 * The registry stores only its digest (B24) — no read operation returns
 * it and no §11 operation can retrieve it afterwards; losing it means
 * revoke + re-enroll.
 */
/**
 * B413 — the §9.1 pairing loop's missing server half (RULED, Pathik
 * 2026-09-02, option (i)). An un-paired device ANNOUNCES the short code it
 * is showing so the back office's enroll can find it: request carries the
 * code alone (the device holds no tenant and no credential), the response
 * is the SAME empty shape for every outcome — fresh, colliding, malformed
 * — because any differentiated answer is an oracle (Inv 10's sibling).
 * The pending pool is tenant-less and short-TTL; device.enroll binds the
 * tenant at consume; a collision leaves the first announcement standing
 * and the device re-mints per boot. The handler's per-source rate wall is
 * the flood wall, and it FAILS CLOSED when unwired (the B103 stance).
 */
export const deviceAnnounce = publicTenantlessOp({
  name: "device.announce",
  permission: "public",
  fields: { code: z.string().min(1).max(32) },
  response: z.object({}).strict(),
  exampleRequest: { code: "PAIR-7Q4D" },
  exampleResponse: {},
});

/**
 * B627 (e) — what the venue's staff call the device ("Showroom door iPad"):
 * 1–60 chars with something besides spaces in it. Trimming is the
 * handler's (the MCP membrane translates no zod `trim`).
 */
/** B1069 — the cap the admin says at the field ("This name is 75 characters — a device's name keeps up to 60."). */
export const DEVICE_NAME_MAX = 60;
const zDeviceName = z.string().min(1).max(DEVICE_NAME_MAX).regex(/\S/);

export const deviceEnroll = writeOp({
  name: "device.enroll",
  permission: "device:write",
  principals: ["STAFF:ADMIN"],
  gate: "admin",
  fields: { deviceCode: z.string().min(1).max(DEVICE_CODE_MAX), role: zDeviceRole, locationId: zId, name: zDeviceName.optional() },
  response: z
    .object({
      deviceId: zId,
      credentialId: zId,
      /** One-time delivery; never retrievable afterwards. */
      credentialSecret: z.string().min(1),
      credentialExpiresAt: zEpochMs,
    })
    .strict(),
  exampleRequest: { deviceCode: "PAIR-1234", role: "VALIDATOR", locationId: "loc-1" },
  exampleResponse: {
    deviceId: "dev-1",
    credentialId: "cred-1",
    credentialSecret: "s3cr3t-delivered-once",
    credentialExpiresAt: 1_780_086_400_000,
  },
});

const zDeviceSummary = z
  .object({ deviceId: zId, role: zDeviceRole, locationId: zId, status: zDeviceStatus, lastSeenAt: zEpochMs.optional(), name: z.string().optional() })
  .strict();

export const deviceList = readOp({
  name: "device.list",
  permission: "device:read",
  principals: ["STAFF:ADMIN"],
  fields: { locationId: zId.optional(), role: zDeviceRole.optional() },
  response: z.object({ devices: z.array(zDeviceSummary) }).strict(),
  exampleRequest: {},
  exampleResponse: { devices: [{ deviceId: "dev-1", role: "VALIDATOR", locationId: "loc-1", status: "ACTIVE" }] },
});

export const deviceGet = readOp({
  name: "device.get",
  permission: "device:read",
  principals: ["STAFF:ADMIN"],
  fields: { deviceId: zId },
  response: z.object({ device: zDeviceSummary.optional() }).strict(),
  exampleRequest: { deviceId: "dev-1" },
  exampleResponse: { device: { deviceId: "dev-1", role: "VALIDATOR", locationId: "loc-1", status: "ACTIVE" } },
});

/** Immediate credential kill — a lost scanner is one call from inert (§11). */
/**
 * B1090 — the longest revoke reason this door keeps, in characters. It had
 * no cap (any length was stored and read back to people); 500 is
 * `entitlement.void`'s, the nearest sibling. Named so every door that types
 * one judges at the same number.
 */
export const DEVICE_REVOKE_REASON_MAX = 500;

export const deviceRevoke = writeOp({
  name: "device.revoke",
  permission: "device:write",
  principals: ["STAFF:ADMIN"],
  fields: { deviceId: zId, reason: z.string().min(1).max(DEVICE_REVOKE_REASON_MAX).optional() },
  response: z.object({ deviceId: zId, status: z.literal("REVOKED") }).strict(),
  exampleRequest: { deviceId: "dev-1" },
  exampleResponse: { deviceId: "dev-1", status: "REVOKED" },
});

/**
 * B434 (M18) — the stage's PUBLIC ticket-verify keys, served to the device
 * audiences so the native validator FETCHES at pairing what the web
 * validator BAKES at build (VITE_TICKET_VERIFY_KEYS — the same
 * `ticketDelivery.verifyKeysJson` source). Public halves ONLY: the strict
 * response has no field a secret could ride, and the handler factory
 * refuses key material that is not an SPKI public half (the B31 stance).
 * An EMPTY key set is a valid answer and means "scanning is off on this
 * stage" (B128) — never a 500. Ruled by Richard 2026-09-09 (cross-cutting
 * M18 answer); Pathik may overrule after the fact.
 */
export const deviceVerifyKeys = readOp({
  name: "device.verify_keys",
  permission: "verifykey:read",
  principals: ["DEVICE:VALIDATOR", "DEVICE:KIOSK"],
  fields: {},
  response: z
    .object({
      /** keyId → base64 SPKI DER (Ed25519 public half). */
      keys: z.record(z.string().min(1), z.string().min(1)),
      generatedAt: zEpochMs,
    })
    .strict(),
  exampleRequest: {},
  exampleResponse: {
    keys: { "key-1": "MCowBQYDK2VwAyEAbN2fDavCNnGUq8osLl4NmRjWqsVBcXb3PSEX7OQfR3M=" },
    generatedAt: 1_780_000_000_000,
  },
});
